import React, { useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaInstagram, FaFacebookF, FaTiktok } from 'react-icons/fa';
import BurgerMenu from '../BurgerMenu/BurgerMenu';
import { useMenu } from '../../contexts/MenuContext';
import Icon from '../Icon/Icon';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background-color: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing.xs} 0;
  box-shadow: ${({ theme }) => theme.shadows.small};
  z-index: 1000;
    
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.xs} 0;
  }
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: ${({ theme }) => theme.breakpoints.wide};
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
  position: relative;
  height: 140px;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    justify-content: center;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 90px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0 ${({ theme }) => theme.spacing.sm};
    height: 80px;
  }
`;

const LogoLink = styled(Link)`
  position: absolute;
  left: ${({ theme }) => theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  z-index: 1002;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    left: ${({ theme }) => theme.spacing.sm};
  }
`;

const Logo = styled.img`
  margin-top: 20px;
  height: 120px;
  width: auto;
  transition: ${({ theme }) => theme.transitions.default};
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));

  &:hover {
    transform: scale(1.05);
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15));
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 70px;
    margin-top: 10px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 60px;
    margin-top: 8px;
  }
`;

/* Desktop Navigation - only visible on tablet+ */
const DesktopNavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

/* Mobile Navigation Drawer - only visible on mobile/tablet */
const MobileNavDrawer = styled.div<{ isOpen: boolean }>`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: flex;
    position: fixed;
    top: 0;
    right: 0;
    width: 280px;
    height: 100vh;
    height: 100dvh;
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-left: 1px solid rgba(38, 108, 127, 0.2);
    box-shadow: -8px 0 32px rgba(0, 0, 0, 0.15);
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    padding: 0 ${({ theme }) => theme.spacing.lg};
    padding-top: 140px;
    padding-bottom: calc(${({ theme }) => theme.spacing.xl} + env(safe-area-inset-bottom, 0px));
    gap: ${({ theme }) => theme.spacing.sm};
    z-index: 1001;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    transform: translateX(${({ isOpen }) => (isOpen ? '0' : '100%')});
    transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 85vw;
    max-width: 300px;
    padding: 0 ${({ theme }) => theme.spacing.md};
    padding-top: 120px;
    padding-bottom: calc(${({ theme }) => theme.spacing.xl} + env(safe-area-inset-bottom, 0px));
  }
`;

const DesktopNavItem = styled(Link) <{ isActive: boolean }>`
  color: ${({ theme, isActive }) => isActive ? theme.colors.accent : theme.colors.background};
  font-weight: ${({ isActive }) => isActive ? '600' : '500'};
  cursor: pointer;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  transition: ${({ theme }) => theme.transitions.default};
  text-decoration: none;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
    background-color: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);

    &:before {
      left: 100%;
    }
  }
`;

const MobileNavItem = styled(Link) <{ isActive: boolean }>`
  color: ${({ theme, isActive }) => isActive ? theme.colors.accent : theme.colors.primary};
  font-weight: ${({ isActive }) => isActive ? '600' : '500'};
  cursor: pointer;
  padding: ${({ theme }) => `${theme.spacing.lg} ${theme.spacing.md}`};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ isActive }) => isActive ? 'rgba(255, 143, 49, 0.1)' : 'transparent'};
  border-left: ${({ isActive, theme }) => isActive ? `4px solid ${theme.colors.accent}` : '4px solid transparent'};
  font-size: 1.1rem;
  text-align: left;
  text-decoration: none;
  transition: ${({ theme }) => theme.transitions.default};
  -webkit-tap-highlight-color: transparent;
  z-index: 1002;

  &:hover {
    background: rgba(38, 108, 127, 0.1);
    border-left-color: ${({ theme }) => theme.colors.primary};
    transform: translateX(4px);
    color: ${({ theme }) => theme.colors.primary};
  }

  &:active {
    background: rgba(38, 108, 127, 0.15);
    transform: translateX(2px);
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid rgba(38, 108, 127, 0.2);
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
`;


const NavItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  flex: 1;
`;

const MobileDeveloperLink = styled.a`
  color: ${({ theme }) => theme.colors.textLight};
  font-size: 0.7rem;
  font-weight: 400;
  text-decoration: none;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  text-align: center;
  opacity: 0.5;
  transition: ${({ theme }) => theme.transitions.default};
  border-top: 1px solid rgba(38, 108, 127, 0.1);
  margin-top: auto;
  padding-bottom: calc(${({ theme }) => theme.spacing.md} + env(safe-area-inset-bottom, 0px));

  &:hover {
    opacity: 0.7;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const DrawerSocialSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.md} 0;
  margin-top: auto;
  border-top: 1px solid rgba(38, 108, 127, 0.1);
`;

const DrawerSocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: rgba(38, 108, 127, 0.08);
  color: ${({ theme }) => theme.colors.primary};
  font-size: 18px;
  transition: all 0.3s ease;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;

  &:hover, &:active {
    background: ${({ theme }) => theme.colors.accent};
    color: white;
    transform: scale(1.1);
  }
`;

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 999;
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  -webkit-tap-highlight-color: transparent;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const Header: React.FC = () => {
  const { isMenuOpen, setIsMenuOpen } = useMenu();
  const location = useLocation();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const closeMenu = useCallback(() => setIsMenuOpen(false), [setIsMenuOpen]);

  const isActive = (path: string) => location.pathname === path;

  // Close menu when route changes
  useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);

  // Prevent body scroll when menu is open (iOS-safe)
  useEffect(() => {
    if (isMenuOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const overlayVariants = {
    closed: {
      opacity: 0,
      transition: { duration: 0.25 }
    },
    open: {
      opacity: 1,
      transition: { duration: 0.25 }
    }
  };

  return (
    <HeaderContainer>
      <Nav>
        <LogoLink to="/">
          <Logo src="/radyofone-logo.png" alt="Radyofone - Free Radio Streaming App" />
        </LogoLink>

        <BurgerMenu isOpen={isMenuOpen} onClick={toggleMenu} />

        {/* Desktop Navigation */}
        <DesktopNavLinks>
          <DesktopNavItem to="/" isActive={isActive('/')}>Home</DesktopNavItem>
          <DesktopNavItem to="/faq" isActive={isActive('/faq')}>FAQ</DesktopNavItem>
          <DesktopNavItem to="/contact" isActive={isActive('/contact')}>Contact Us</DesktopNavItem>
          <DesktopNavItem to="/privacy-policy" isActive={isActive('/privacy-policy')}>Privacy Policy</DesktopNavItem>
          <DesktopNavItem to="/account-deletion" isActive={isActive('/account-deletion')}>Delete Account</DesktopNavItem>
        </DesktopNavLinks>

        {/* Mobile Overlay */}
        <AnimatePresence>
          {isMenuOpen && (
            <Overlay
              variants={overlayVariants}
              initial="closed"
              animate="open"
              exit="closed"
              onClick={closeMenu}
            />
          )}
        </AnimatePresence>

        {/* Mobile Navigation Drawer */}
        <MobileNavDrawer isOpen={isMenuOpen}>
          <DrawerHeader>

          </DrawerHeader>
          <NavItemsContainer>
            <MobileNavItem to="/" isActive={isActive('/')} onClick={closeMenu}>Home</MobileNavItem>
            <MobileNavItem to="/faq" isActive={isActive('/faq')} onClick={closeMenu}>FAQ</MobileNavItem>
            <MobileNavItem to="/contact" isActive={isActive('/contact')} onClick={closeMenu}>Contact Us</MobileNavItem>
            <MobileNavItem to="/privacy-policy" isActive={isActive('/privacy-policy')} onClick={closeMenu}>Privacy Policy</MobileNavItem>
            <MobileNavItem to="/account-deletion" isActive={isActive('/account-deletion')} onClick={closeMenu}>Delete Account</MobileNavItem>
          </NavItemsContainer>
          <DrawerSocialSection>
            <DrawerSocialLink
              href="https://www.instagram.com/radyo_fone"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <Icon icon={FaInstagram} size={18} />
            </DrawerSocialLink>
            <DrawerSocialLink
              href="https://www.facebook.com/share/1AdYHXENLj/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
            >
              <Icon icon={FaFacebookF} size={18} />
            </DrawerSocialLink>
            <DrawerSocialLink
              href="https://www.tiktok.com/@radyofone"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
            >
              <Icon icon={FaTiktok} size={18} />
            </DrawerSocialLink>
          </DrawerSocialSection>
          <MobileDeveloperLink
            href="https://www.quantumbytech.com"
            target="_blank"
            rel="noopener noreferrer"
            onClick={closeMenu}
          >
            Developed by quantumbytech
          </MobileDeveloperLink>
        </MobileNavDrawer>
      </Nav>
    </HeaderContainer>
  );
};

export default Header;